import { type CSSProperties, type HTMLAttributes, type ReactNode, useCallback, useEffect, useState } from "react";
import type { VisibleNode } from "../index";
import { useViewer } from "./context";
export interface NodeOverlayProps extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
  minScale?: number;
  maxScale?: number;
  maxNodes?: number;
  filter?: (node: VisibleNode) => boolean;
  interactive?: boolean;
  style?: CSSProperties;
  children: (node: VisibleNode) => ReactNode;
}
export function NodeOverlay({
  minScale = 0,
  maxScale = Number.POSITIVE_INFINITY,
  maxNodes = 200,
  filter,
  interactive = false,
  className,
  style,
  children,
  ...divProps
}: NodeOverlayProps) {
  const viewer = useViewer();
  const [nodes, setNodes] = useState<readonly VisibleNode[]>([]);
  const update = useCallback(() => {
    const scale = viewer.getCameraState().scale;
    if (scale < minScale || scale > maxScale || maxNodes <= 0) {
      setNodes(current => current.length === 0 ? current : []);
      return;
    }
    const visible = viewer.getVisibleNodes();
    const filtered = filter ? visible.filter(filter) : visible;
    setNodes(filtered.slice(0, Math.floor(maxNodes)));
  }, [filter, maxNodes, maxScale, minScale, viewer]);
  useEffect(() => {
    const unsubscribeViewport = viewer.subscribe("viewport", update);
    const unsubscribeGraph = viewer.subscribe("graph", update);
    update();
    return () => {
      unsubscribeViewport();
      unsubscribeGraph();
    };
  }, [update, viewer]);
  return <div {...divProps} className={className} style={{
    position: "absolute",
    inset: 0,
    overflow: "hidden",
    pointerEvents: "none",
    ...style
  }}>
      {nodes.map(node => <div key={node.id} data-viewer-node-id={node.id} style={{
      position: "absolute",
      left: 0,
      top: 0,
      width: node.screenWidth,
      height: node.screenHeight,
      transform: `translate(${node.screenX}px, ${node.screenY}px)`,
      pointerEvents: interactive ? "auto" : "none"
    }}>
          {children(node)}
        </div>)}
    </div>;
}